import React, { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Mail, MapPin, Clock, Phone } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const Contact = () => {
  const { language } = useLanguage();
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const content = {
    fr: {
      hero: {
        title: 'Contactez-nous',
        subtitle: 'Une question ? Un litige à résoudre ? Notre équipe est là pour vous aider.'
      },
      form: {
        title: 'Envoyez-nous un message',
        name: 'Nom complet',
        namePlaceholder: 'Jean Dupont',
        email: 'Email',
        emailPlaceholder: 'jean.dupont@example.com',
        phone: 'Téléphone (optionnel)',
        phonePlaceholder: '+33 6 12 34 56 78',
        service: 'Service intéressé',
        servicePlaceholder: 'Sélectionnez un service',
        services: [
          'Pack Modèles Propriétaire',
          'Audit Express de votre contrat',
          'Accompagnement Litige',
          'Autre / Question générale'
        ],
        message: 'Votre message',
        messagePlaceholder: 'Décrivez votre situation ou posez votre question...',
        submit: 'Envoyer le message',
        submitting: 'Envoi en cours...',
        success: 'Message envoyé avec succès !',
        successDescription: 'Nous vous répondrons dans les 24 heures.',
        error: 'Erreur lors de l\'envoi',
        errorDescription: 'Veuillez réessayer plus tard.'
      },
      info: {
        title: 'Informations de contact',
        email: 'Email',
        emailValue: 'contact@deposecure.com',
        location: 'Localisation',
        locationValue: 'France',
        hours: 'Horaires',
        hoursValue: 'Lun-Ven: 9h-18h',
        response: 'Temps de réponse',
        responseValue: 'Moins de 24h'
      },
      payment: {
        title: 'Paiement en ligne disponible',
        description: 'Vous pouvez payer directement en ligne de manière sécurisée via notre page de paiement.',
        note: ''
      }
    },
    en: {
      hero: {
        title: 'Contact Us',
        subtitle: 'A question? A dispute to resolve? Our team is here to help you.'
      },
      form: {
        title: 'Send us a message',
        name: 'Full name',
        namePlaceholder: 'John Doe',
        email: 'Email',
        emailPlaceholder: 'john.doe@example.com',
        phone: 'Phone (optional)',
        phonePlaceholder: '+33 6 12 34 56 78',
        service: 'Service interested in',
        servicePlaceholder: 'Select a service',
        services: [
          'Owner Templates Pack',
          'Express Contract Audit',
          'Dispute Support',
          'Other / General Question'
        ],
        message: 'Your message',
        messagePlaceholder: 'Describe your situation or ask your question...',
        submit: 'Send message',
        submitting: 'Sending...',
        success: 'Message sent successfully!',
        successDescription: 'We will respond within 24 hours.',
        error: 'Error sending message',
        errorDescription: 'Please try again later.'
      },
      info: {
        title: 'Contact Information',
        email: 'Email',
        emailValue: 'contact@deposecure.com',
        location: 'Location',
        locationValue: 'France',
        hours: 'Hours',
        hoursValue: 'Mon-Fri: 9am-6pm',
        response: 'Response time',
        responseValue: 'Less than 24h'
      },
      payment: {
        title: 'Online Payment Available',
        description: 'You can pay directly online securely through our payment page.',
        note: ''
      }
    }
  };

  const t = content[language];

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const API = `${BACKEND_URL}/api`;
      
      const response = await fetch(`${API}/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (data.success) {
        toast({
          title: t.form.success,
          description: t.form.successDescription,
        });
        
        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          service: '',
          message: ''
        });
      } else {
        throw new Error(data.message || 'Submission failed');
      }
    } catch (error) {
      console.error('Error submitting contact form:', error);
      toast({
        title: t.form.error,
        description: t.form.errorDescription,
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-slate-50 via-blue-50 to-slate-50 py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
            {t.hero.title}
          </h1>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            {t.hero.subtitle}
          </p>
        </div>
      </section>

      {/* Contact Form and Info */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Contact Form */}
            <div className="lg:col-span-2">
              <Card className="shadow-lg">
                <CardContent className="p-8">
                  <h2 className="text-2xl font-bold text-slate-900 mb-6">{t.form.title}</h2>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                      <Label htmlFor="name">{t.form.name} *</Label>
                      <Input
                        id="name"
                        name="name"
                        type="text"
                        required
                        placeholder={t.form.namePlaceholder}
                        value={formData.name}
                        onChange={handleChange}
                        className="mt-2"
                      />
                    </div>

                    <div>
                      <Label htmlFor="email">{t.form.email} *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        required
                        placeholder={t.form.emailPlaceholder}
                        value={formData.email}
                        onChange={handleChange}
                        className="mt-2"
                      />
                    </div>

                    <div>
                      <Label htmlFor="phone">{t.form.phone}</Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        placeholder={t.form.phonePlaceholder}
                        value={formData.phone}
                        onChange={handleChange}
                        className="mt-2"
                      />
                    </div>

                    <div>
                      <Label htmlFor="service">{t.form.service} *</Label>
                      <select
                        id="service"
                        name="service"
                        required
                        value={formData.service}
                        onChange={handleChange}
                        className="mt-2 w-full px-3 py-2 border border-slate-300 rounded-lg bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-900"
                      >
                        <option value="">{t.form.servicePlaceholder}</option>
                        {t.form.services.map((service, index) => (
                          <option key={index} value={service}>
                            {service}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <Label htmlFor="message">{t.form.message} *</Label>
                      <Textarea
                        id="message"
                        name="message"
                        required
                        placeholder={t.form.messagePlaceholder}
                        value={formData.message}
                        onChange={handleChange}
                        className="mt-2 min-h-[150px]"
                      />
                    </div>

                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-blue-900 hover:bg-blue-800 text-white"
                      size="lg"
                    >
                      {isSubmitting ? t.form.submitting : t.form.submit}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>

            {/* Contact Info */}
            <div className="space-y-8">
              <Card className="shadow-lg">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-slate-900 mb-6">{t.info.title}</h3>
                  <div className="space-y-6">
                    <div className="flex items-start space-x-3">
                      <Mail className="h-5 w-5 text-blue-900 mt-1" />
                      <div>
                        <div className="font-semibold text-slate-900">{t.info.email}</div>
                        <a
                          href={`mailto:${t.info.emailValue}`}
                          className="text-blue-900 hover:underline"
                        >
                          {t.info.emailValue}
                        </a>
                      </div>
                    </div>

                    <div className="flex items-start space-x-3">
                      <MapPin className="h-5 w-5 text-blue-900 mt-1" />
                      <div>
                        <div className="font-semibold text-slate-900">{t.info.location}</div>
                        <div className="text-slate-600">{t.info.locationValue}</div>
                      </div>
                    </div>

                    <div className="flex items-start space-x-3">
                      <Clock className="h-5 w-5 text-blue-900 mt-1" />
                      <div>
                        <div className="font-semibold text-slate-900">{t.info.hours}</div>
                        <div className="text-slate-600">{t.info.hoursValue}</div>
                      </div>
                    </div>

                    <div className="flex items-start space-x-3">
                      <Phone className="h-5 w-5 text-blue-900 mt-1" />
                      <div>
                        <div className="font-semibold text-slate-900">{t.info.response}</div>
                        <div className="text-slate-600">{t.info.responseValue}</div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Payment Info */}
              <Card className="shadow-lg bg-blue-50 border-blue-200">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-slate-900 mb-4">{t.payment.title}</h3>
                  <p className="text-slate-700 mb-4">{t.payment.description}</p>
                  <a
                    href="/payment"
                    className="inline-block mt-2 text-blue-900 font-medium hover:underline"
                  >
                    {language === 'fr' ? 'Accéder au paiement en ligne →' : 'Access online payment →'}
                  </a>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
